import React from 'react';
import { Link } from 'react-router-dom';
import { Flame, ThumbsUp, ThumbsDown, MessageSquare, ExternalLink, Clock, ShoppingBag } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { pl } from 'date-fns/locale';

export function DealCard({ deal, onReact, userReaction }) {
  const displayPrice = deal.deal_price;
  const displayDesc = deal.ai_description || deal.description;
  const imageUrl = deal.image_local_path
    ? `${process.env.REACT_APP_BACKEND_URL}${deal.image_local_path}`
    : deal.image_url;
  const buyUrl = deal.affiliate_url || deal.url;
  
  const timeAgo = deal.created_at
    ? formatDistanceToNow(new Date(deal.created_at), { addSuffix: true, locale: pl })
    : '';

  const hotCount = deal.reactions?.hot || 0;
  const coldCount = deal.reactions?.cold || 0;
  const temperature = hotCount - coldCount;

  return (
    <div
      className="bg-card border border-border/40 deal-card-hover overflow-hidden animate-slide-up"
      data-testid={`deal-card-${deal.id}`}
    >
      <div className="flex flex-col sm:flex-row">
        {/* Image */}
        {imageUrl && (
          <div className="sm:w-48 h-40 sm:h-auto flex-shrink-0 relative overflow-hidden bg-secondary">
            <img
              src={imageUrl}
              alt={deal.title}
              className="w-full h-full object-cover"
              loading="lazy"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            {displayPrice && (
              <div className="absolute bottom-2 left-2 price-glow px-3 py-1.5">
                <span className="price-tag text-2xl">{displayPrice} zł</span>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 p-4 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <Link
                to={`/okazja/${deal.slug || deal.id}`}
                className="hover:text-primary transition-colors"
                data-testid={`deal-link-${deal.id}`}
              >
                <h3 className="font-heading font-bold text-lg text-white leading-tight line-clamp-2">
                  {deal.title}
                </h3>
              </Link>
              <div className="flex items-center gap-2 mt-1.5 text-xs text-muted-foreground">
                <span>{deal.user_nick || deal.external_author || 'Anonim'}</span>
                <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                <Clock className="w-3 h-3" />
                <span>{timeAgo}</span>
                {deal.category && (
                  <>
                    <span className="w-1 h-1 rounded-full bg-muted-foreground/50" />
                    <span className="text-primary/80">{deal.category}</span>
                  </>
                )}
              </div>
            </div>
            
            {/* Temperature */}
            <div className={`flex flex-col items-center px-3 py-2 ${
              temperature > 0 ? 'text-red-500' : temperature < 0 ? 'text-blue-400' : 'text-muted-foreground'
            }`}>
              <Flame className="w-5 h-5" strokeWidth={temperature > 0 ? 2.5 : 1.5} />
              <span className="font-heading font-bold text-sm">{temperature > 0 ? '+' : ''}{temperature}</span>
            </div>
          </div>

          {/* Description */}
          <p className="text-sm text-muted-foreground mt-2 line-clamp-2">
            {displayDesc}
          </p>

          {/* Price (no image) */}
          {!imageUrl && displayPrice && (
            <div className="mt-2">
              <span className="price-glow inline-block px-3 py-1">
                <span className="price-tag text-2xl">{displayPrice} zł</span>
              </span>
              {deal.original_price && deal.deal_price && deal.original_price > deal.deal_price && (
                <span className="text-xs text-muted-foreground line-through ml-2">{deal.original_price} zł</span>
              )}
            </div>
          )}

          {/* Actions bar */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-border/30">
            <div className="flex items-center gap-3">
              <button
                data-testid={`react-hot-${deal.id}`}
                onClick={(e) => { e.preventDefault(); onReact?.(deal.id, 'hot'); }}
                className={`flex items-center gap-1 text-xs transition-colors ${
                  userReaction === 'hot' ? 'text-red-500' : 'text-muted-foreground hover:text-red-500'
                }`}
              >
                <ThumbsUp className="w-4 h-4" />
                <span>{hotCount}</span>
              </button>
              <button
                data-testid={`react-cold-${deal.id}`}
                onClick={(e) => { e.preventDefault(); onReact?.(deal.id, 'cold'); }}
                className={`flex items-center gap-1 text-xs transition-colors ${
                  userReaction === 'cold' ? 'text-blue-400' : 'text-muted-foreground hover:text-blue-400'
                }`}
              >
                <ThumbsDown className="w-4 h-4" />
                <span>{coldCount}</span>
              </button>
              <Link
                to={`/okazja/${deal.slug || deal.id}`}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-white transition-colors"
                data-testid={`comments-link-${deal.id}`}
              >
                <MessageSquare className="w-4 h-4" />
                <span>{deal.comments_count || 0}</span>
              </Link>
            </div>

            {/* BUY NOW button */}
            {buyUrl && (
              <a
                href={buyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="buy-now-btn inline-flex items-center gap-1.5 font-heading font-bold uppercase tracking-wider text-sm"
                data-testid={`deal-buy-${deal.id}`}
                onClick={(e) => e.stopPropagation()}
              >
                <ShoppingBag className="w-4 h-4" /> KUP TERAZ
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
